import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";


/* 🔔 Toast */
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ⚠️ Sweet Alert */
import Swal from "sweetalert2";

/* 🎯 Icons */
import {
  FaCheckCircle,
  FaTimesCircle,
  FaBan,
  FaVideo,
  FaCalendarAlt,
  FaClock,
  FaEnvelope,
  FaFileContract,
  FaComments,
  FaSync,
  FaBalanceScale,
  FaWindowClose,
  FaExclamationTriangle
} from "react-icons/fa";
import {
  MdOutlinePendingActions,
  MdOutlineCheckCircleOutline,
  MdOutlineCancel,
  MdOutlineEventAvailable,
  MdOutlineAccessTime
} from "react-icons/md";
import { MdOutlineFormatListNumbered } from "react-icons/md";
import {
  FiDownload,
} from "react-icons/fi";
import "../styles/SolicitorAppointments.css";

export default function SolicitorAppointments() {
  const { token } = useAuth();

  const [appointments, setAppointments] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [planName, setPlanName] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  // 🔥 COMPLETED SUB TABS
  const [completedTab, setCompletedTab] = useState("court");

  // Stats
  const [stats, setStats] = useState({
    pending: 0,
    court: 0,
    dismissed: 0,
    rejected: 0
  });

  /* ======================
     FETCH APPOINTMENTS
  ====================== */
  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/api/appointments/solicitor/my`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setAppointments(data.appointments || []);

      // Calculate stats with updated logic
      const pendingCount = data.appointments?.filter(app =>
        app.status === "PENDING" ||
        (app.status === "ACCEPTED" && app.appointmentCallStatus === "NOT_STARTED") ||
        (app.status === "ACCEPTED" &&
          app.appointmentCallStatus === "COMPLETED" &&
          app.caseStatus === "NONE")
      ).length || 0;

      const courtCount = data.appointments?.filter(app =>
        app.status === "ACCEPTED" &&
        app.appointmentCallStatus === "COMPLETED" &&
        app.caseStatus === "FILED_IN_COURT"
      ).length || 0;

      const dismissedCount = data.appointments?.filter(app =>
        app.status === "ACCEPTED" &&
        app.appointmentCallStatus === "COMPLETED" &&
        app.caseStatus === "DISMISSED"
      ).length || 0;

      const rejectedCount = data.appointments?.filter(app =>
        app.status === "REJECTED"
      ).length || 0;

      setStats({
        pending: pendingCount,
        court: courtCount,
        dismissed: dismissedCount,
        rejected: rejectedCount
      });

    } catch (error) {
      console.error("Fetch appointments error:", error);
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPlan = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();

      setPlanName(data?.user?.subscription?.planName || "FREE");

    } catch (err) {
      console.error("Plan fetch error:", err);
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetchUserPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createAgreement = (appointmentId) => {
    // 1️⃣ Save appointment ID
    sessionStorage.setItem(
      "selected_appointment_for_agreement",
      appointmentId
    );

    // 2️⃣ Tell dashboard to open Agreements page
    sessionStorage.setItem(
      "solicitor_active_section",
      "agreements"
    );

    // 3️⃣ Reload dashboard to apply section change
    window.location.reload();
  };

  const getEndTime = (startDate, durationMinutes) => {
    const d = new Date(startDate);
    d.setMinutes(d.getMinutes() + (durationMinutes || 0));
    return d;
  };



  // 🔹 Export Excel
  const handleExport = async () => {
    if (planName === "FREE") {
      toast.error("Upgrade plan to export Excel");
      return;
    }
    if (currentList.length === 0) {
      toast.warning("No data to export");
      return;
    }

    let type = "pending";

    if (activeTab === "pending") {
      type = "pending";
    }

    if (activeTab === "completed") {
      if (completedTab === "court") type = "court";
      if (completedTab === "dismissed") type = "dismissed";
      if (completedTab === "rejected") type = "rejected";
    }

    try {
      setExportLoading(true);

      const res = await fetch(
        `${API_BASE_URL}/api/appointments/solicitor/export/excel?type=${type}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) throw new Error("Export failed");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      // 🔥 File name by tab
      if (type === "pending") link.download = "Pending_Appointments.xlsx";
      if (type === "court") link.download = "Court_Cases.xlsx";
      if (type === "dismissed") link.download = "Dismissed_Cases.xlsx";
      if (type === "rejected") link.download = "Rejected_Appointments.xlsx";

      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Excel exported successfully");

    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExportLoading(false);
    }
  };

  /* ======================
     TOGGLE DETAILS
  ====================== */
  const toggleDetails = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setRejectReason("");
  };

  /* ======================
     FILTER LOGIC (UPDATED)
  ====================== */
  const pendingList = appointments.filter(app =>
    // Pending review
    app.status === "PENDING" ||
    // Accepted but meeting not started yet
    (app.status === "ACCEPTED" && app.appointmentCallStatus === "NOT_STARTED") ||
    // Meeting completed but NOT yet filed in court or dismissed
    (app.status === "ACCEPTED" &&
      app.appointmentCallStatus === "COMPLETED" &&
      app.caseStatus === "NONE")
  );

  const completedBase = appointments.filter(app =>
    app.status === "ACCEPTED" &&
    app.appointmentCallStatus === "COMPLETED" &&
    (app.caseStatus === "FILED_IN_COURT" || app.caseStatus === "DISMISSED")
  );

  const courtList = completedBase.filter(app =>
    app.caseStatus === "FILED_IN_COURT"
  );

  const dismissedList = completedBase.filter(app =>
    app.caseStatus === "DISMISSED"
  );

  const rejectedList = appointments.filter(app =>
    app.status === "REJECTED"
  );

  let currentList = [];

  if (activeTab === "pending") currentList = pendingList;
  if (activeTab === "completed") {
    if (completedTab === "court") currentList = courtList;
    if (completedTab === "dismissed") currentList = dismissedList;
    if (completedTab === "rejected") currentList = rejectedList;
  }

  /* ======================
     ACTIONS WITH ALERTS
  ====================== */
  const updateStatus = async (id, status) => {
    if (status === "REJECTED" && !rejectReason.trim()) {
      toast.error("Please enter rejection reason");
      return;
    }

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${status.toLowerCase()} this appointment`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Continue",
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#6b7280"
    });

    if (!confirm.isConfirmed) return;

    try {
      await fetch(`${API_BASE_URL}/api/appointments/${id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status,
          rejectionReason: status === "REJECTED" ? rejectReason : null
        })
      });

      toast.success(`Appointment ${status.toLowerCase()} successfully`);
      fetchAppointments();
      setExpandedId(null);
    } catch (error) {
      toast.error("Failed to update appointment");
    }
  };

  const markCompleted = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/appointments/${id}/complete`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Meeting marked as completed");
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to mark as completed");
    }
  };

  // eslint-disable-next-line no-unused-vars
  const moveToCourt = async (id) => {
    const confirm = await Swal.fire({
      title: "Move to Court?",
      text: "This case will be filed in court",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Move",
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#6b7280"
    });

    if (!confirm.isConfirmed) return;

    try {
      await fetch(`${API_BASE_URL}/api/appointments/${id}/move-to-court`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Case moved to court successfully");
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to move to court");
    }
  };

  const dismissCase = async (id) => {
    const confirm = await Swal.fire({
      title: "Dismiss Case?",
      text: "This case will be dismissed permanently",
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Yes, Dismiss",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280"
    });

    if (!confirm.isConfirmed) return;

    try {
      await fetch(`${API_BASE_URL}/api/appointments/${id}/dismiss`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Case dismissed successfully");
      fetchAppointments();
    } catch (error) {
      toast.error("Failed to dismiss case");
    }
  };

  /* ======================
     RENDER STATUS BADGE (UPDATED)
  ====================== */
  const renderStatusBadge = (app) => {
    let badgeConfig = {
      icon: <FaExclamationTriangle size={14} />,
      label: "Unknown",
      color: "#6b7280",
      bgColor: "#f3f4f6"
    };

    if (app.status === "PENDING") {
      badgeConfig = {
        icon: <MdOutlinePendingActions size={14} />,
        label: "Pending Review",
        color: "#f59e0b",
        bgColor: "#fef3c7"
      };
    } else if (app.status === "ACCEPTED") {
      if (app.appointmentCallStatus === "NOT_STARTED") {
        badgeConfig = {
          icon: <MdOutlineAccessTime size={14} />,
          label: "Scheduled",
          color: "#3b82f6",
          bgColor: "#dbeafe"
        };
      } else if (app.appointmentCallStatus === "COMPLETED") {
        if (app.caseStatus === "NONE") {
          badgeConfig = {
            icon: <FaCheckCircle size={14} />,
            label: "Meeting Completed",
            color: "#10b981",
            bgColor: "#d1fae5"
          };
        } else if (app.caseStatus === "FILED_IN_COURT") {
          badgeConfig = {
            icon: <FaBalanceScale size={14} />,
            label: "Filed in Court",
            color: "#8b5cf6",
            bgColor: "#ede9fe"
          };
        } else if (app.caseStatus === "DISMISSED") {
          badgeConfig = {
            icon: <FaWindowClose size={14} />,
            label: "Case Dismissed",
            color: "#dc2626",
            bgColor: "#fee2e2"
          };
        }
      }
    } else if (app.status === "REJECTED") {
      badgeConfig = {
        icon: <FaTimesCircle size={14} />,
        label: "Rejected",
        color: "#dc2626",
        bgColor: "#fee2e2"
      };
    }

    return (
      <span
        className="solicitor-status-badge"
        style={{
          backgroundColor: badgeConfig.bgColor,
          color: badgeConfig.color
        }}
      >
        {badgeConfig.icon}
        <span>{badgeConfig.label}</span>
      </span>
    );
  };

  /* ======================
     RENDER STATS CARDS
  ====================== */
  const renderStatsCards = () => (
    <div className="solicitor-stats-grid">
      <div className="solicitor-stat-card solicitor-stat-pending">
        <div className="solicitor-stat-icon">
          <MdOutlinePendingActions size={24} />
        </div>
        <div className="solicitor-stat-content">
          <h3 className="solicitor-stat-value">{stats.pending}</h3>
          <p className="solicitor-stat-label">Pending</p>
          <span className="solicitor-stat-subtext">Awaiting Action</span>
        </div>
      </div>

      <div className="solicitor-stat-card solicitor-stat-court">
        <div className="solicitor-stat-icon">
          <FaBalanceScale size={24} />
        </div>
        <div className="solicitor-stat-content">
          <h3 className="solicitor-stat-value">{stats.court}</h3>
          <p className="solicitor-stat-label">Court Cases</p>
          <span className="solicitor-stat-subtext">Filed in Court</span>
        </div>
      </div>

      <div className="solicitor-stat-card solicitor-stat-dismissed">
        <div className="solicitor-stat-icon">
          <FaWindowClose size={24} />
        </div>
        <div className="solicitor-stat-content">
          <h3 className="solicitor-stat-value">{stats.dismissed}</h3>
          <p className="solicitor-stat-label">Dismissed</p>
          <span className="solicitor-stat-subtext">Closed Cases</span>
        </div>
      </div>

      <div className="solicitor-stat-card solicitor-stat-rejected">
        <div className="solicitor-stat-icon">
          <MdOutlineCancel size={24} />
        </div>
        <div className="solicitor-stat-content">
          <h3 className="solicitor-stat-value">{stats.rejected}</h3>
          <p className="solicitor-stat-label">Rejected</p>
          <span className="solicitor-stat-subtext">Cancelled Appointments</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="solicitor-appointments-wrapper">
      {/* 🔔 Toast Container */}
      <ToastContainer position="top-right" autoClose={2500} />

      {/* 🔹 HEADER */}
      <div className="solicitor-appointments-header">
        <div className="solicitor-header-left">
          <h1 className="solicitor-page-title">My Appointments</h1>
          <p className="solicitor-page-subtitle">
            Manage client consultations and case progression
          </p>
        </div>
        <div className="solicitor-header-right">
          <button
            className="solicitor-refresh-btn"
            onClick={fetchAppointments}
            disabled={loading}
          >
            <FaSync size={16} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
          <button
            className="solicitor-export-btn"
            onClick={handleExport}
            disabled={exportLoading || currentList.length === 0 || planName === "FREE"}
            title={currentList.length === 0 ? "No data to export" : planName === "FREE" ? "Upgrade plan to export Excel" : "Export to Excel"}
          >
            {exportLoading ? (
              <>
                <div className="solicitor-export-btn-spinner"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <FiDownload size={18} />
                <span style={{ color: "white" }}>
                  Export ({currentList.length})
                </span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 🔥 STATS */}
      {renderStatsCards()}

      {/* 🔥 MAIN CONTENT */}
      <div className="solicitor-appointments-content">
        {/* Tabs */}
        <div className="solicitor-tabs-container">
          {/* Main Tabs */}
          <div className="solicitor-main-tabs">
            {["pending", "completed"].map(tab => (
              <button
                key={tab}
                className={`solicitor-tab-btn ${activeTab === tab ? "solicitor-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setExpandedId(null);
                  if (tab === "completed") {
                    setCompletedTab("court");
                  }
                }}
              >
                {tab === "pending" && <MdOutlinePendingActions size={16} />}
                {tab === "completed" && <MdOutlineCheckCircleOutline size={16} />}
                <span>
                  {tab === "pending" && `Pending (${stats.pending})`}
                  {tab === "completed" && `Completed (${stats.court + stats.dismissed + stats.rejected})`}
                </span>
              </button>
            ))}
          </div>

          {/* Sub Tabs for Completed */}
          {activeTab === "completed" && (
            <div className="solicitor-sub-tabs">
              {[
                { key: "court", icon: <FaBalanceScale size={14} />, label: "Court Cases" },
                { key: "dismissed", icon: <FaWindowClose size={14} />, label: "Dismissed" },
                { key: "rejected", icon: <FaTimesCircle size={14} />, label: "Rejected" }
              ].map(subTab => (
                <button
                  key={subTab.key}
                  className={`solicitor-sub-tab-btn ${completedTab === subTab.key ? "solicitor-sub-tab-active" : ""}`}
                  onClick={() => setCompletedTab(subTab.key)}
                >
                  {subTab.icon}
                  <span>
                    {subTab.label} ({stats[subTab.key]})
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 🔥 LIST */}
        <div className="solicitor-list-container">
          {loading ? (
            <div className="solicitor-loading-state">
              <div className="solicitor-loading-spinner"></div>
              <p>Loading appointments...</p>
            </div>
          ) : currentList.length === 0 ? (
            <div className="solicitor-empty-state">
              <MdOutlineEventAvailable size={48} />
              <p>No appointments in this section</p>
              <small>Try selecting a different tab or check back later</small>
            </div>
          ) : (
            <div className="solicitor-cards-grid">
              {currentList.map(app => (
                <div key={app._id} className="solicitor-appointment-card">
                  {/* Card Header */}
                  <div className="solicitor-card-header">
                    <div className="solicitor-client-info">
                      <div className="solicitor-client-avatar">
                        {app.userId?.userProfile?.photo ? (
                          <img
                            src={app.userId.userProfile.photo}
                            alt={app.userId.name}
                            className="client-avatar-img"
                          />
                        ) : (
                          <div className="client-avatar-fallback">
                            {app.userDetails?.name?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="solicitor-client-details">
                        <div className="solicitor-client-name">
                          {app.userDetails?.name || "Client Name Not Available"}
                        </div>
                        <div className="solicitor-client-meta">
                          <FaEnvelope size={12} />
                          <span>{app.userDetails?.email || "No email provided"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="solicitor-status-container">
                      {renderStatusBadge(app)}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="solicitor-card-body">
                    <div className="solicitor-datetime-info">
                      <div className="solicitor-date-info">
                        <FaCalendarAlt size={14} />
                        <span>
                          {new Date(app.appointmentDate).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="solicitor-time-info">
                        <FaClock size={14} />
                        <span>
                          {new Date(app.appointmentDate).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                          {" - "}
                          {getEndTime(
                            app.appointmentDate,
                            app.appointmentDurationMinutes
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </span>
                      </div>

                      {/* 🔥 Appointment ID Display */}
                      <p className="solicitor-appointment-id">
                        <strong>{app.appointmentId}</strong>
                      </p>
                    </div>

                    <div className="solicitor-case-info">
                      <div className="solicitor-case-category">
                        <FaFileContract size={14} />
                        <span>{app.practiceCategory || "General Consultation"}</span>
                      </div>
                      <div className="solicitor-case-type">
                        <FaComments size={14} />
                        <span>{app.caseType || "Case Type"}</span>
                      </div>
                      <div className="solicitor-case-type">
                        <MdOutlineFormatListNumbered size={14} />
                        <span>Slot : {app.slotNumber || "Not Assigned"}</span>
                      </div>
                    </div>

                    <div className="solicitor-actions">
                      <button
                        className="solicitor-view-details-btn"
                        onClick={() => toggleDetails(app._id)}
                      >
                        {expandedId === app._id ? "Hide Details" : "View Details"}
                      </button>

                      {app.status === "ACCEPTED" && app.appointmentCallStatus === "NOT_STARTED" && (
                        <button
                          className="solicitor-join-call-btn"
                          onClick={async () => {
                            try {
                              // 🔥 Update solicitor join status first
                              await fetch(
                                `${API_BASE_URL}/api/appointments/call-status/${app._id}`,
                                {
                                  method: "PATCH",
                                  headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`
                                  },
                                  body: JSON.stringify({ status: "JOINED" })
                                }
                              );

                              // 🔥 Open meeting after status update
                              window.open(app.videoCallLink, "_blank");

                            } catch (err) {
                              console.error("Join meeting error:", err);
                              toast.error("Failed to start meeting");
                            }
                          }}
                        >
                          <FaVideo size={16} />
                          <span>Join Video Call</span>
                        </button>
                      )}

                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === app._id && (
                    <div className="solicitor-expanded-details">
                      <div className="solicitor-details-section">
                        <h4>Case Details</h4>
                        <div className="solicitor-details-grid">
                          <div className="solicitor-detail-item">
                            <strong>Practice Area:</strong>
                            <span>{app.practiceArea || "Not specified"}</span>
                          </div>
                          <div className="solicitor-detail-item">
                            <strong>Practice Category:</strong>
                            <span>{app.practiceCategory || "Not specified"}</span>
                          </div>
                          <div className="solicitor-detail-item">
                            <strong>Case Type:</strong>
                            <span>{app.caseType || "Not specified"}</span>
                          </div>
                          <div className="solicitor-detail-item">
                            <strong>Booked On:</strong>
                            <span>{new Date(app.createdAt).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="solicitor-description-section">
                        <h4>Case Description</h4>
                        <div className="solicitor-description-content">
                          {app.caseDescription || "No description provided."}
                        </div>
                      </div>

                      {/* Action Buttons Based on Status - UPDATED LOGIC */}
                      <div className="solicitor-action-section">
                        {/* PENDING APPOINTMENTS */}
                        {app.status === "PENDING" && (
                          <div className="solicitor-pending-actions">
                            <button
                              className="solicitor-accept-btn"
                              onClick={() => updateStatus(app._id, "ACCEPTED")}
                            >
                              <FaCheckCircle size={16} />
                              <span>Accept Appointment</span>
                            </button>

                            <div className="solicitor-reject-container">
                              <textarea
                                className="solicitor-reject-textarea"
                                placeholder="Enter reason for rejection (required)"
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows="3"
                              />
                              <button
                                className="solicitor-reject-btn"
                                onClick={() => updateStatus(app._id, "REJECTED")}
                                disabled={!rejectReason.trim()}
                              >
                                <FaTimesCircle size={16} />
                                <span>Reject Appointment</span>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* ACCEPTED & MEETING NOT STARTED YET */}
                        {app.status === "ACCEPTED" && app.appointmentCallStatus === "NOT_STARTED" && (
                          <div className="solicitor-scheduled-actions">
                            <button
                              className="solicitor-complete-btn"
                              onClick={() => markCompleted(app._id)}
                            >
                              <FaCheckCircle size={16} />
                              <span>Mark as Completed</span>
                            </button>
                          </div>
                        )}

                        {/* MEETING COMPLETED BUT NOT YET PROCESSED - STAYS IN PENDING TAB */}
                        {app.status === "ACCEPTED" &&
                          app.appointmentCallStatus === "COMPLETED" &&
                          app.caseStatus === "NONE" && (
                            <div className="solicitor-completed-actions">
                              <div className="solicitor-case-progression">
                                <button
                                  className="solicitor-court-btn"
                                  onClick={() => createAgreement(app.appointmentId)}
                                >
                                  <FaFileContract size={16} />
                                  <span>Create Agreement</span>
                                </button>

                                <button
                                  className="solicitor-dismiss-btn"
                                  onClick={() => dismissCase(app._id)}
                                >
                                  <FaBan size={16} />
                                  <span>Dismiss Case</span>
                                </button>
                              </div>

                            </div>
                          )}

                        {/* ALREADY FILED IN COURT - SHOWS IN COMPLETED TAB */}
                        {app.status === "ACCEPTED" &&
                          app.appointmentCallStatus === "COMPLETED" &&
                          app.caseStatus === "FILED_IN_COURT" && (
                            <div className="solicitor-court-status">
                              <div className="solicitor-status-indicator solicitor-court-indicator">
                                <FaBalanceScale size={18} />
                                <span>Case Filed in Court</span>
                              </div>
                            </div>
                          )}

                        {/* ALREADY DISMISSED - SHOWS IN COMPLETED TAB */}
                        {app.status === "ACCEPTED" &&
                          app.appointmentCallStatus === "COMPLETED" &&
                          app.caseStatus === "DISMISSED" && (
                            <div className="solicitor-dismissed-status">
                              <div className="solicitor-status-indicator solicitor-dismissed-indicator">
                                <FaWindowClose size={18} />
                                <span>Case Dismissed</span>
                              </div>
                            </div>
                          )}

                        {/* REJECTED APPOINTMENTS */}
                        {app.status === "REJECTED" && app.rejectionReason && (
                          <div className="solicitor-rejected-info">
                            <h4>Rejection Details</h4>
                            <div className="solicitor-rejection-content">
                              <strong>Reason:</strong> {app.rejectionReason}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}